import { useState } from 'react';
import type { ModelRecord } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useDeprecateModel, useUndeprecateModel } from '../api/useModels.ts';
import { useToast, coerceErrorMessage } from './Toast.tsx';
import { useT } from '../i18n/index.ts';

/**
 * Model deprecation card — deprecate / undeprecate. Epic #109 P2.12.
 *
 * Deprecation is the softer intermediate state between "live" and
 * "soft-deleted". A deprecated model stays listable / chargeable /
 * routable but is flagged in `ModelRecord.deprecated_at` so any UI
 * that cares can surface a migration notice.
 *
 * Lifecycle is separate from PATCH so the audit log has clean
 * `model.deprecate` / `model.undeprecate` rows rather than opaque
 * `model.update` diffs. Notice updates require undeprecate → deprecate
 * (two audit rows) rather than in-place edits.
 */

const DEPRECATION_NOTICE_MAX = 512;

interface Props {
  readonly record: ModelRecord;
}

export function DeprecationCard({ record }: Props): JSX.Element {
  const t = useT();
  const deprecate = useDeprecateModel();
  const undeprecate = useUndeprecateModel();
  const toast = useToast();
  const [notice, setNotice] = useState('');
  const [error, setError] = useState<string | null>(null);

  const busy = deprecate.isPending || undeprecate.isPending;
  const isDeprecated = record.deprecated_at !== null;

  async function handleDeprecate(): Promise<void> {
    setError(null);
    const trimmed = notice.trim();
    if (trimmed.length === 0) {
      setError(t('model_detail.deprecation.error.notice_required'));
      return;
    }
    if (trimmed.length > DEPRECATION_NOTICE_MAX) {
      setError(
        t('model_detail.deprecation.error.notice_too_long', {
          limit: DEPRECATION_NOTICE_MAX,
        }),
      );
      return;
    }
    try {
      await deprecate.mutateAsync({
        vendor: record.vendor,
        model: record.model,
        body: { deprecation_notice: trimmed },
      });
      setNotice('');
      toast.success(t('model_detail.deprecation.toast.deprecated'));
    } catch (err) {
      const message =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  async function handleUndeprecate(): Promise<void> {
    setError(null);
    try {
      await undeprecate.mutateAsync({ vendor: record.vendor, model: record.model });
      toast.success(t('model_detail.deprecation.toast.undeprecated'));
    } catch (err) {
      const message =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <article
      data-testid="model-deprecation-card"
      data-deprecated={isDeprecated ? 'true' : 'false'}
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center gap-3">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('model_detail.deprecation.title')}
        </h3>
      </header>
      <p className="text-sm text-outline">{t('model_detail.deprecation.body')}</p>
      {isDeprecated ? (
        <div
          data-testid="model-deprecation-state"
          className="flex flex-col gap-3 rounded-lg bg-surface-container-low p-4"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-tertiary-container px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-on-tertiary-container">
              {t('model_detail.deprecation.chip.deprecated')}
            </span>
            <span
              className="font-mono text-[11px] text-outline"
              data-testid="model-deprecation-stamp"
            >
              {record.deprecated_at}
            </span>
          </div>
          {record.deprecation_notice !== null ? (
            <p
              className="whitespace-pre-wrap text-sm text-on-surface"
              data-testid="model-deprecation-notice"
            >
              {record.deprecation_notice}
            </p>
          ) : null}
          <div>
            <button
              type="button"
              onClick={() => void handleUndeprecate()}
              disabled={busy}
              data-testid="model-undeprecate"
              className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
            >
              {undeprecate.isPending
                ? t('model_detail.deprecation.undeprecate.pending')
                : t('model_detail.deprecation.undeprecate.submit')}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('model_detail.deprecation.notice.label')}
            </span>
            <textarea
              value={notice}
              onChange={(e) => setNotice(e.currentTarget.value)}
              disabled={busy}
              data-testid="model-deprecation-notice-input"
              rows={3}
              maxLength={DEPRECATION_NOTICE_MAX}
              placeholder={t('model_detail.deprecation.notice.placeholder')}
              className="w-full rounded-lg bg-surface-container-lowest px-3 py-2 text-sm text-on-surface border-0 border-b border-outline-variant focus:border-primary focus:outline-none focus:ring-0 transition-colors disabled:opacity-40"
            />
            <span
              className="text-[10px] text-outline"
              data-testid="model-deprecation-char-count"
            >
              {t('model_detail.deprecation.notice.counter', {
                count: notice.length,
                limit: DEPRECATION_NOTICE_MAX,
              })}
            </span>
          </label>
          <div>
            <button
              type="button"
              onClick={() => void handleDeprecate()}
              disabled={busy || notice.trim().length === 0}
              data-testid="model-deprecate"
              className="inline-flex items-center justify-center rounded-lg bg-tertiary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-tertiary-container transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
            >
              {deprecate.isPending
                ? t('model_detail.deprecation.deprecate.pending')
                : t('model_detail.deprecation.deprecate.submit')}
            </button>
          </div>
        </div>
      )}
      {error !== null ? (
        <p
          className="text-xs font-medium text-error"
          role="alert"
          data-testid="model-deprecation-error"
        >
          {error}
        </p>
      ) : null}
    </article>
  );
}
